// MegaMenu

import React, { Children } from 'react';
import { navigate } from 'gatsby';
import { Check, ArrowRight } from 'lucide-react';

// List Column Component

export const MegaMenuListColumn = ({ 
  title, 
  items, 
  showSeparators = true,
  hasScrolled, 
  onItemClick, 
  closeMenu 
}) => {

  const handleItemClick = (item) => {
    // First, call any custom onItemClick handler if provided
    if (onItemClick) {
      onItemClick(item);
    }

    // Then navigate to the specified URI
    if (item.uri) {
      navigate(item.uri);
    }

    // Close the menu if a close function is provided
    if (closeMenu) {
      closeMenu();
    }
  };

  return (
    <div className={`${ showSeparators? "border-r border-gray-200":""} px-6`}>
      <h3 className={`text-lg font-bold mb-2 ${
        hasScrolled ? 'text-purple-700' : 'text-white'
      }`}>
        {title}
      </h3>
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.id}>
            <button 
              onClick={() => handleItemClick(item)}
              className={`text-left w-full p-2 rounded hover:text-purple-700 transition ${
                hasScrolled ? 'text-gray-700 hover:bg-purple-50' : 'text-gray-200 hover:bg-purple-400'
              }`}
            >
              <div className="font-medium">{item.title}</div>
              <div className={`text-sm ${
                hasScrolled ? 'text-gray-500' : 'text-purple-200'
              }`}>
                {item.description}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Section Column Component for multiple vertical sections
export const MegaMenuSectionColumn = ({ 
  children,
  showSeparators = true,
  separatorColor,
  hasScrolled 
}) => {
  // Default separator color based on scroll state
  const defaultSeparatorColor = hasScrolled 
    ? 'border-gray-200' 
    : 'border-purple-300/50';
  
  const finalSeparatorColor = separatorColor || defaultSeparatorColor;

  // Convert children to array to handle both single and multiple children
  const childrenArray = Children.toArray(children);

  return (
    <div className="border-r border-gray-200 px-6">
      {childrenArray.map((child, index) => (
        <div key={index}>
          <div className="pb-2">
            {/* Clone the child element and pass showSeparators={false} */}
            {React.cloneElement(child, { showSeparators: false })}
          </div>
          {/* Show separator except after the last section */}
          {showSeparators && index < childrenArray.length - 1 && (
            <div className={`border-t ${finalSeparatorColor} mx-2 mb-2`} />
          )}
        </div>
      ))}
    </div>
  );
};

// Arbitrary Column Component
export const MegaMenuColumn = ({ children }) => {
  return <div>{children}</div>;
};

// Main MegaMenu Component
export const MegaMenu = ({ 
  children, 
  hasScrolled,
  width = 800,  
  more
}) => {
  // Determine the number of columns dynamically
  const columnCount = Children.count(children);

  // Dynamic grid columns based on child count
  const gridColumnClass = columnCount === 2 
    ? 'grid-cols-2' 
    : columnCount === 3 
    ? 'grid-cols-3' 
    : `grid-cols-${columnCount}`;

  return (
    <div 
      className={`absolute left-1/2 transform -translate-x-1/2 mt-0 border rounded-xl shadow-2xl z-50 ${
        hasScrolled ? 'bg-white' : 'bg-purple-900/95'
      }`}
	  style={{ width: `${width}px` }}

    >
      <div className={`grid ${gridColumnClass} gap-0 p-6`}>
        {children}
      </div>
	  { more && (
			<div 
				className={`px-6 py-3 rounded-xl ${
				  hasScrolled ? 'bg-gray-50' : 'bg-purple-800'
				}`}
			  >
			  { more } 
			</div>
	   )
	}
    </div>
  );
};